import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useDomainStore, useFamilyStore, useSuggestionsStore } from '@/hooks/useStores';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import SuggestionDiffPreview from '@/components/suggestions/SuggestionDiffPreview';
import SuggestionEvidencePanel from '@/components/suggestions/SuggestionEvidencePanel';
import Spinner from '@/components/shared/Spinner';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILY_MY_SUGGESTIONS_TOUR_STEPS,
  FAMILY_MY_SUGGESTIONS_TOUR_STORAGE_KEY,
  resolveFamilyMySuggestionsTourSteps
} from '@/data/family-my-suggestions-tour';
import { formatStoryDate } from '@/types/story';
import { suggestionStatusLabel } from '@/types/suggestion';

import './family-my-suggestions-screen.css';

type StatusFilter = 'all' | 'pending' | 'changes_requested' | 'approved' | 'rejected' | 'cancelled';

const FamilyMySuggestionsScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const suggestionsStore = useSuggestionsStore();
  const navigate = useNavigate();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const [searchParams] = useSearchParams();
  const targetIdFilter = searchParams.get('targetId') || undefined;

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const family = domainStore.currentFamily;
  const familyDisplayName = family?.displayName ?? family?.name ?? t('reviewQueue.familyFallback');
  const selectedId = suggestionsStore.selectedSuggestionId;
  const selectedDetail = suggestionsStore.selectedSuggestionDetail;
  const selectedPreview = suggestionsStore.myList.find((item) => item.id === selectedId);

  const filterOptions: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: t('mySuggestions.filterAll') },
    { id: 'pending', label: t('mySuggestions.filterPending') },
    { id: 'changes_requested', label: t('mySuggestions.filterChangesRequested') },
    { id: 'approved', label: t('mySuggestions.filterApproved') },
    { id: 'rejected', label: t('mySuggestions.filterRejected') },
    { id: 'cancelled', label: t('mySuggestions.filterCancelled') }
  ];

  const loadList = useCallback(async () => {
    if (!familyIdParam) return;
    await suggestionsStore.loadMySuggestions(familyIdParam, { targetId: targetIdFilter });
  }, [familyIdParam, suggestionsStore, targetIdFilter]);

  const suggestionsTour = useScreenTour({
    steps: FAMILY_MY_SUGGESTIONS_TOUR_STEPS,
    completedStorageKey: FAMILY_MY_SUGGESTIONS_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyMySuggestionsTourSteps(steps, suggestionsStore.myList.length > 0)
  });

  useEffect(() => {
    if (!familyIdParam) return;
    domainStore.setSelectedFamily(familyIdParam);
    if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
      void familyStore.loadFamilyData(familyIdParam);
    }
  }, [domainStore, familyIdParam, familyStore]);

  useEffect(() => {
    void loadList();
  }, [loadList, suggestionsStore.myListFilters.status, suggestionsStore.myListFilters.page]);

  useEffect(() => {
    if (!familyIdParam || !selectedId) return;
    void suggestionsStore.loadSuggestionDetail(familyIdParam, selectedId);
  }, [familyIdParam, selectedId, suggestionsStore]);

  const handleFilterChange = (status: StatusFilter) => {
    suggestionsStore.setMyListStatusFilter(status);
  };

  const handleSelect = (id: string) => {
    setActionError(null);
    suggestionsStore.selectSuggestion(id);
  };

  const handleCancel = async () => {
    if (!familyIdParam || !selectedId) return;
    if (!confirm(t('mySuggestions.cancelConfirm'))) return;
    setActionError(null);
    const result = await suggestionsStore.cancelSuggestion(familyIdParam, selectedId);
    if (!result.success) {
      setActionError(suggestionsStore.error ?? t('mySuggestions.cancelFailed'));
      return;
    }
    await loadList();
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      ...domainStore.currentFamily,
      id: familyIdParam,
      displayName,
      alternativeName
    });
    setShowEditNameModal(false);
  };

  const canCancel =
    selectedDetail && (selectedDetail.status === 'pending' || selectedDetail.status === 'changes_requested');

  const targetLabel =
    selectedDetail?.targetType === 'family_member'
      ? familyStore.getPerson(selectedDetail.targetId)?.fullName ||
        familyStore.getPet(selectedDetail.targetId)?.displayName ||
        null
      : null;

  if (!familyIdParam) {
    return (
      <div className="my-suggestions-page">
        <p className="my-suggestions-page__error">{t('mySuggestions.noFamily')}</p>
      </div>
    );
  }

  return (
    <div className="my-suggestions-page">
      <FamilyPageHeader
        familyDisplayName={familyDisplayName}
        sectionTitle={t('mySuggestions.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={suggestionsTour.startTour}
        introTourId="suggestions-intro"
        intro={
          <>
            {t('mySuggestions.lede')} <ScreenHelpDot />
          </>
        }
      />

      {targetIdFilter ? (
        <p className="my-suggestions-page__filter-note">
          {t('mySuggestions.filterNote')}{' '}
          <button type="button" className="btn-text" onClick={() => navigate(`/families/${familyIdParam}/suggestions`)}>
            {t('mySuggestions.clearFilter')}
          </button>
        </p>
      ) : null}

      <div className="my-suggestions-page__filter-bar" data-tour="suggestions-filters">
        <div className="list-seg" role="tablist" aria-label={t('mySuggestions.filtersAria')}>
          {filterOptions.map((option) => {
            const active = suggestionsStore.myListFilters.status === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`list-seg__btn${active ? ' list-seg__btn--active' : ''}`}
                onClick={() => handleFilterChange(option.id)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {suggestionsStore.myListError && (
        <p className="my-suggestions-page__error" role="alert">
          {suggestionsStore.myListError}
        </p>
      )}
      {actionError && (
        <p className="my-suggestions-page__error" role="alert">
          {actionError}
        </p>
      )}

      <div className="my-suggestions-page__layout">
        <div className="my-suggestions-page__list-panel" data-tour="suggestions-list">
          {suggestionsStore.isLoadingMyList ? (
            <div className="my-suggestions-page__loading">
              <Spinner />
            </div>
          ) : suggestionsStore.myList.length === 0 ? (
            <p className="my-suggestions-page__empty">{t('mySuggestions.empty')}</p>
          ) : (
            <ul className="my-suggestions-page__list">
              {suggestionsStore.myList.map((item) => {
                const active = item.id === selectedId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`my-suggestions-page__list-item${active ? ' my-suggestions-page__list-item--active' : ''}`}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => handleSelect(item.id)}
                    >
                      <span className="my-suggestions-page__item-status">{suggestionStatusLabel(item.status)}</span>
                      <span className="my-suggestions-page__item-title">
                        {item.title || t('mySuggestions.untitled')}
                      </span>
                      <span className="my-suggestions-page__item-date">{formatStoryDate(item.createdAt)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {suggestionsStore.myListTotalPages > 1 && (
            <div className="my-suggestions-page__pagination">
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={suggestionsStore.myListFilters.page <= 1}
                onClick={() => suggestionsStore.setMyListPage(suggestionsStore.myListFilters.page - 1)}
              >
                {tForms('actions.previous')}
              </button>
              <span className="my-suggestions-page__page-label">
                {t('mySuggestions.pageOf', {
                  page: suggestionsStore.myListFilters.page,
                  total: suggestionsStore.myListTotalPages
                })}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={suggestionsStore.myListFilters.page >= suggestionsStore.myListTotalPages}
                onClick={() => suggestionsStore.setMyListPage(suggestionsStore.myListFilters.page + 1)}
              >
                {tForms('actions.next')}
              </button>
            </div>
          )}
        </div>

        <div className="my-suggestions-page__detail-panel" data-tour="suggestions-detail">
          {!selectedId ? (
            <p className="my-suggestions-page__detail-placeholder">{t('mySuggestions.selectPlaceholder')}</p>
          ) : suggestionsStore.isLoadingDetail ? (
            <Spinner />
          ) : suggestionsStore.detailError ? (
            <p className="my-suggestions-page__error">{suggestionsStore.detailError}</p>
          ) : selectedDetail ? (
            <>
              <div className="my-suggestions-page__detail-header">
                <span className="my-suggestions-page__item-status">{suggestionStatusLabel(selectedDetail.status)}</span>
                <h2 className="my-suggestions-page__detail-title">
                  {selectedDetail.title || t('mySuggestions.untitled')}
                </h2>
                {targetLabel ? (
                  <p className="my-suggestions-page__detail-target">
                    {t('mySuggestions.about')}{' '}
                    <Link to={`/families/${familyIdParam}/people/${selectedDetail.targetId}`}>{targetLabel}</Link>
                  </p>
                ) : null}
                {selectedDetail.description ? (
                  <p className="my-suggestions-page__author-note">{selectedDetail.description}</p>
                ) : null}
                {selectedDetail.status === 'changes_requested' ? (
                  <div className="my-suggestions-page__changes-callout" role="status">
                    <p className="my-suggestions-page__changes-callout-title">
                      {t('mySuggestions.changesRequestedTitle')}
                    </p>
                    {selectedDetail.resolutionComment ? (
                      <p className="my-suggestions-page__changes-callout-note">
                        <span className="my-suggestions-page__changes-callout-label">
                          {t('mySuggestions.theirNote')}
                        </span>{' '}
                        {selectedDetail.resolutionComment}
                      </p>
                    ) : (
                      <p className="my-suggestions-page__changes-callout-note">{t('mySuggestions.noWrittenNote')}</p>
                    )}
                    <p className="my-suggestions-page__changes-callout-guidance">{t('mySuggestions.reviseGuidance')}</p>
                  </div>
                ) : selectedDetail.resolutionComment ? (
                  <p className="my-suggestions-page__resolution">
                    {t('mySuggestions.reviewerNote', { comment: selectedDetail.resolutionComment })}
                  </p>
                ) : null}
              </div>

              <div className="my-suggestions-page__detail-section">
                <h3 className="my-suggestions-page__detail-section-title">{t('mySuggestions.proposedChanges')}</h3>
                <SuggestionDiffPreview diffPayload={selectedDetail.diffPayload} />
              </div>

              <div className="my-suggestions-page__detail-section">
                <SuggestionEvidencePanel suggestion={selectedDetail} />
              </div>

              {canCancel ? (
                <div className="my-suggestions-page__actions">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={suggestionsStore.isCancelling}
                    onClick={() => void handleCancel()}
                  >
                    {suggestionsStore.isCancelling ? tForms('actions.cancelling') : tForms('actions.cancelSuggestion')}
                  </button>
                </div>
              ) : null}
            </>
          ) : selectedPreview ? (
            <p className="my-suggestions-page__detail-placeholder">{t('mySuggestions.loadDetailFailed')}</p>
          ) : null}
        </div>
      </div>

      {showEditNameModal && family && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={family.displayName}
          canonicalName={family.name}
          hasAlternative={!!family.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={suggestionsTour} />
    </div>
  );
});

export default FamilyMySuggestionsScreen;
